import React, { useState, useEffect } from "react";
import { Box, Typography as MuiTypography, Card, CardContent, Divider } from "@mui/material";
import DashboardLayout from "../../layout/DashboardLayout/DashboardLayout";
import { Input, DatePicker, Row, Col, ConfigProvider, message, Spin, Button, Form, Typography, Select, Pagination } from "antd";
import dayjs from "dayjs";
import service from "../../services";
import { SearchOutlined } from "@ant-design/icons";

import successCheckIcon from "../../assets/icons/success_check.png";
import errorExclamationIcon from "../../assets/icons/error_exclamation.png";
import warningAmberIcon from "../../assets/icons/warning_amber.png";
import "../../index.css";
import infoHourglassIcon from "../../assets/icons/info_hourglass.png";
import rocketGreenIcon from "../../assets/icons/rocket_green.png";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const Notifications = () => {
  const [pushForm] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingPush, setSendingPush] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [userOptionsLoading, setUserOptionsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  useEffect(() => {
    fetchAllUsers();
    fetchNotifications();
  }, []);

  const fetchAllUsers = async () => {
    setUserOptionsLoading(true);
    try {
      const res = await service.getUsersList(200, 1, "asc");
      const list = res?.data?.data?.activeUsers || [];
      setUserOptions(
        list.map((u) => ({
          label: `${u.user?.firstName} ${u.user?.lastName} (${u.user?.email})`,
          value: u.user?.id || u.id,
        }))
      );
    } catch (e) {
      console.error("Failed to load users:", e);
    } finally {
      setUserOptionsLoading(false);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const startDate = dateRange?.[0]?.format("YYYY-MM-DD");
      const endDate = dateRange?.[1]?.format("YYYY-MM-DD");
      const response = await service.getNotifications(currentPage, pageSize, searchText, startDate, endDate);
      const rawData = response.data?.data || [];
      setNotifications(rawData.map(item => ({
        id: item.id,
        title: item.notificationHeading,
        description: item.notificationMessage,
        date: dayjs(item.createdAt).format("DD/MM/YY h:mm a"),
        createdAt: item.createdAt,
        type: mapNotificationType(item.type),
      })));
    } catch (error) {
      console.error("Error fetching notifications:", error);
      message.error("Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  const mapNotificationType = (apiType) => {
    switch (apiType) {
      case "TRIP_SUBMISSION":
        return "info";
      case "CHALLENGE_CREATED":
      case "CHALLENGE_ACTIVATION":
        return "activation";
      case "CHALLENGE_UPDATED":
      case "WEEKLY_VERIFICATION":
      case "NEW_USER_JOINED":
      case "CHALLENGE_COMPLETED":
        return "success";
      case "CHALLENGE_ENDING":
      case "LOW_PARTICIPATION":
      case "USER_DROPPED":
      case "TRIP_UPDATED":
        return "warning";
      case "DEADLINE_REACHED":
      case "CHALLENGE_DELETED":
      case "ENGAGEMENT_UPDATE":
        return "error";
      default:
        return "info";
    }
  };

  const filteredData = notifications.filter(item => {
    const matchesSearch = !searchText ||
      item.title?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchText.toLowerCase());

    let matchesDate = true;
    if (dateRange?.[0] && dateRange?.[1]) {
      const itemDate = new Date(item.createdAt);
      matchesDate = itemDate >= dateRange[0].startOf('day').toDate() &&
        itemDate <= dateRange[1].endOf('day').toDate();
    }

    return matchesSearch && matchesDate;
  });

  const data = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSendPushNotification = async () => {
    try {
      const values = await pushForm.validateFields();
      setSendingPush(true);
      const payload = {
        title: values.title?.trim(),
        body: values.body?.trim(),
        userId: values.dataUserId ? Number(values.dataUserId) : undefined,
      };
      await service.sendPushNotification(payload);
      message.success("Push notification sent");
      pushForm.resetFields();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error("Error sending push notification:", error);
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      message.error(typeof errMsg === "string" ? errMsg : "Failed to send notification");
    } finally {
      setSendingPush(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "activation":
        return (
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#ECFDF3', // Green-50 matching success
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <img src={rocketGreenIcon} alt="Activation" style={{ width: '30px', height: '30px' }} />
          </div>
        );
      case "success":
        return (
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#ECFDF3', // Green-50
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <img src={successCheckIcon} alt="Success" style={{ width: '30px', height: '30px' }} />
          </div>
        );
      case "error":
        return (
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#FEF3F2', // Red-50
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <img src={errorExclamationIcon} alt="Error" style={{ width: '30px', height: '30px' }} />
          </div>
        );
      case "warning":
        return (
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#FFFAEB', // Amber-50
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <img src={warningAmberIcon} alt="Warning" style={{ width: '30px', height: '30px' }} />
          </div>
        );
      case "info":
      default:
        return (
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#EFF8FF', // Blue-50
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <img src={infoHourglassIcon} alt="Info" style={{ width: '30px', height: '30px' }} />
          </div>
        );
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#5FB92A",
          fontFamily: "'Manrope', sans-serif",
          borderRadius: 8,
          colorText: "#101828",
          colorTextSecondary: "#667085",
        },
        components: {
          Form: {
            labelFontSize: 14,
            labelColor: "#344054",
            verticalLabelPadding: "0 0 8px",
            itemMarginBottom: 20,
          },
        },
      }}
    >
      <DashboardLayout>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Header */}
          <MuiTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Notifications
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            View activity, search and filter updates, and send push notifications to a device.
          </MuiTypography>

          <Card sx={{
            boxShadow: 'none',
            border: '1px solid #EAECF0',
            borderRadius: '12px',
            mb: 4,
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
              <MuiTypography variant="subtitle1" sx={{ fontWeight: 600, color: '#101828', mb: 0.5 }}>
                Send notification
              </MuiTypography>
              <Text type="secondary" style={{ display: 'block', fontSize: 14, marginBottom: 24, lineHeight: 1.5 }}>
                Sends notification to the user.
              </Text>
              <Form
                form={pushForm}
                layout="vertical"
                initialValues={{
                  dataUserId: "",
                }}
              >
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Form.Item
                      name="title"
                      label="Title"
                      rules={[{ required: true, message: "Enter a title" }]}
                    >
                      <Input size="large" placeholder="Enter notification title" style={{ borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Form.Item
                      name="body"
                      label="Body"
                      rules={[{ required: true, message: "Enter body text" }]}
                    >
                      <Input.TextArea rows={3} placeholder="Enter notification message" style={{ borderRadius: 8 }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="dataUserId" label="User">
                      <Select
                        size="large"
                        showSearch
                        allowClear
                        placeholder="Select user"
                        filterOption={(input, option) =>
                          option?.label?.toLowerCase().includes(input.toLowerCase())
                        }
                        loading={userOptionsLoading}
                        notFoundContent={userOptionsLoading ? <Spin size="small" /> : "No users found"}
                        options={userOptions}
                        style={{ borderRadius: 8, width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item style={{ marginBottom: 0, marginTop: 4 }}>
                  <Button
                    type="primary"
                    size="large"
                    loading={sendingPush}
                    onClick={handleSendPushNotification}
                    style={{ borderRadius: 8, backgroundColor: '#5FB92A', minWidth: 200 }}
                  >
                    Send notification
                  </Button>
                </Form.Item>
              </Form>
            </CardContent>
          </Card>

          <Divider sx={{ mb: 3 }} />

          <MuiTypography variant="subtitle1" sx={{ fontWeight: 600, color: '#101828', mb: 2 }}>
            Activity
          </MuiTypography>

          {/* Filters */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }} align="middle" justify="space-between">
            <Col xs={24} md={12}>
              <Input
                size="large"
                placeholder="Search notifications" // As per design text, though 'notifications' makes more sense
                prefix={<SearchOutlined style={{ color: '#667085' }} />}
                style={{ maxWidth: '400px', borderRadius: '8px' }}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
            <Col>
              <RangePicker size="large" onChange={(dates) => { setDateRange(dates); setCurrentPage(1); }} style={{ borderRadius: "8px" }} />
            </Col>
          </Row>

          {/* Notifications List */}
          {loading && (
            <div className="table-progress-bar">
              <div className="table-progress-bar-inner" />
            </div>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!loading && (
              <>
                {data.map((item) => (
                  <Card key={item.id} sx={{
                    boxShadow: 'none',
                    border: '1px solid #EAECF0',
                    borderRadius: '12px',
                    '&:hover': { boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }
                  }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, '&:last-child': { pb: 2 } }}>
                      {getIcon(item.type)}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                          <MuiTypography variant="subtitle1" sx={{ fontWeight: 600, color: '#101828' }}>
                            {item.title}
                          </MuiTypography>
                          <MuiTypography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {item.date}
                          </MuiTypography>
                        </Box>
                        <MuiTypography variant="body2" color="text.secondary">
                          {item.description}
                        </MuiTypography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}

                {data.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 5 }}>
                    <MuiTypography color="text.secondary">No notifications found.</MuiTypography>
                  </Box>
                )}

                {filteredData.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={filteredData.length}
                      showSizeChanger
                      pageSizeOptions={["10", "20", "50"]}
                      showTotal={(total, range) => `${range[0]}–${range[1]} of ${total}`}
                      onChange={(page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </DashboardLayout>
    </ConfigProvider>
  );
};

export default Notifications;
